/**
 * ImageUploadField — reusable image input with three modes:
 *  1. Upload from computer → Supabase Storage → public URL
 *  2. Pick from Media Library (existing uploads)
 *  3. Paste a URL manually
 *
 * Props:
 *  value       — current image URL
 *  onChange    — called with new URL
 *  label       — field label
 *  placeholder — URL input placeholder
 */
import { useState, useRef, useCallback } from "react";
import { useStorageUpload } from "@/hooks/useStorageUpload";
import { useMediaDB } from "@/hooks/useMediaDB";
import type { MediaItem } from "@/mocks/admin";

interface ImageUploadFieldProps {
  value: string;
  onChange: (url: string) => void;
  label?: string;
  placeholder?: string;
  className?: string;
}

/* ─── Media Picker Modal ─────────────────────────────────────────────────── */
interface MediaPickerProps {
  onSelect: (item: MediaItem) => void;
  onClose: () => void;
}

function MediaPickerModal({ onSelect, onClose }: MediaPickerProps) {
  const { media, loading, uploadMedia, uploadProgress } = useMediaDB();
  const [search, setSearch] = useState("");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = media.filter(
    (m) => m.type === "image" && m.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploadError(null);
    for (const file of Array.from(files)) {
      const { error } = await uploadMedia(file);
      if (error) { setUploadError(error); break; }
    }
  }, [uploadMedia]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-hj-gray-border shrink-0">
          <h3 className="text-base font-bold text-hj-dark">Bild auswählen</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-hj-gray-light transition-colors cursor-pointer">
            <i className="ri-close-line text-hj-gray"></i>
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-5 py-3 border-b border-hj-gray-border flex items-center gap-3 shrink-0">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-hj-gray text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Bilder suchen..."
              className="w-full pl-9 pr-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
            />
          </div>
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploadProgress.uploading}
            className="px-3 py-2 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors flex items-center gap-1.5 disabled:opacity-60 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-upload-cloud-line text-sm"></i>
            {uploadProgress.uploading ? "Hochladen..." : "Hochladen"}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>

        {/* Upload error */}
        {uploadError && (
          <div className="mx-5 mt-3 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700 shrink-0">
            <strong>Fehler:</strong> {uploadError}
          </div>
        )}

        {/* Upload progress */}
        {uploadProgress.uploading && (
          <div className="mx-5 mt-3 p-3 bg-hj-red/5 border border-hj-red/20 rounded-md flex items-center gap-3 shrink-0">
            <div className="w-5 h-5 border-2 border-hj-red border-t-transparent rounded-full animate-spin shrink-0"></div>
            <p className="text-sm text-hj-red">Wird hochgeladen: {uploadProgress.fileName}</p>
          </div>
        )}

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-7 h-7 border-2 border-hj-red border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-image-line text-4xl text-hj-gray-border mb-3 block"></i>
              <p className="text-sm text-hj-gray">
                {search ? "Keine Bilder gefunden." : "Noch keine Bilder hochgeladen."}
              </p>
              <p className="text-xs text-hj-gray mt-1">Klicken Sie auf „Hochladen", um Bilder hinzuzufügen.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {filtered.map((item) => (
                <button
                  key={item.id}
                  onClick={() => onSelect(item)}
                  className="group relative aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-hj-red transition-all cursor-pointer bg-hj-gray-light"
                >
                  <img
                    src={item.url}
                    alt={item.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-end">
                    <div className="w-full px-2 py-1.5 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-[10px] text-white truncate">{item.name}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-hj-gray-border shrink-0">
          <p className="text-xs text-hj-gray">{filtered.length} Bilder in der Mediathek</p>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────────── */

export default function ImageUploadField({
  value,
  onChange,
  label = "Bild",
  placeholder = "https://...",
  className = "",
}: ImageUploadFieldProps) {
  const { uploadFile, progress } = useStorageUpload();
  const [showPicker, setShowPicker] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [urlMode, setUrlMode] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploadError(null);
      const { url, error } = await uploadFile(file);
      if (error) { setUploadError(error); }
      else if (url) { onChange(url); }
      e.target.value = "";
    },
    [uploadFile, onChange]
  );

  const handlePickerSelect = useCallback(
    (item: MediaItem) => {
      onChange(item.url);
      setShowPicker(false);
    },
    [onChange]
  );

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-semibold text-hj-dark mb-2">{label}</label>
      )}

      {/* Upload error */}
      {uploadError && (
        <div className="mb-2 p-2.5 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
          <i className="ri-error-warning-line text-red-600 text-sm shrink-0 mt-0.5"></i>
          <p className="text-xs text-red-700 flex-1">{uploadError}</p>
          <button onClick={() => setUploadError(null)} className="text-red-400 hover:text-red-600 cursor-pointer shrink-0">
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>
      )}

      {/* Uploading indicator */}
      {progress.uploading && (
        <div className="mb-2 p-2.5 bg-hj-red/5 border border-hj-red/20 rounded-md flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-hj-red border-t-transparent rounded-full animate-spin shrink-0"></div>
          <p className="text-xs text-hj-red">Wird hochgeladen: {progress.fileName}</p>
        </div>
      )}

      {/* Image preview */}
      {value && (
        <div className="mb-3 relative rounded-lg overflow-hidden bg-hj-gray-light" style={{ height: "180px" }}>
          <img src={value} alt="Vorschau" className="w-full h-full object-cover" />
          <button
            onClick={() => onChange("")}
            className="absolute top-2 right-2 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors cursor-pointer"
            title="Bild entfernen"
          >
            <i className="ri-close-line text-hj-dark text-sm"></i>
          </button>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mb-2">
        {/* Upload from computer */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={progress.uploading}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-hj-gray-border rounded-md hover:bg-hj-gray-light transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-upload-2-line text-sm"></i>
          Vom Computer
        </button>

        {/* Pick from media library */}
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-hj-gray-border rounded-md hover:bg-hj-gray-light transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-image-2-line text-sm"></i>
          Mediathek
        </button>

        {/* Toggle URL input */}
        <button
          type="button"
          onClick={() => setUrlMode((v) => !v)}
          className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border rounded-md transition-colors cursor-pointer whitespace-nowrap ${
            urlMode
              ? "border-hj-red bg-hj-red/5 text-hj-red"
              : "border-hj-gray-border hover:bg-hj-gray-light"
          }`}
        >
          <i className="ri-link text-sm"></i>
          URL eingeben
        </button>
      </div>

      {/* URL input (shown when toggled) */}
      {urlMode && (
        <input
          type="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
        />
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Media picker modal */}
      {showPicker && (
        <MediaPickerModal
          onSelect={handlePickerSelect}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}
